/* global console, crypto */

/**
 * Sentry reporting for a completed styling run.
 *
 * The run's perf report is the only record of where a slow run spent its time,
 * and users cannot share a browser console — so it goes to Sentry as context on
 * a message captured at the end of every run, successful or not.
 */

import * as Sentry from "@sentry/react";
import { getUserEmail } from "src/auth/auth-client";
import { formatDuration, formatPerfReport, selfMs } from "./perf";

import type { PerfEntry, PerfReport } from "./perf";

/**
 * Id for one styling run, sent to the API as `X-MSS-Run-Id` and tagged on both
 * sides' Sentry events so a slow run can be read end to end.
 *
 * 32 lowercase hex characters, because `beginStylingRun` also uses it as the
 * Sentry trace id, which has to be exactly that. `randomUUID` is missing from
 * the WebView in older Word builds, hence the fallback.
 */
const newRunId = (): string =>
  typeof crypto?.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

/**
 * Start a run: mint its id and open a Sentry trace under the same value.
 *
 * The taskpane never navigates, so every run in a session would otherwise
 * inherit the single trace created at load and the API's report could not be
 * tied to one run. Reusing the run id as the trace id means the tag, the
 * `X-MSS-Run-Id` header and the trace are all the same string.
 */
export function beginStylingRun(): string {
  const runId = newRunId();
  Sentry.getCurrentScope().setPropagationContext({
    traceId: runId,
    sampleRand: Math.random(),
  });
  return runId;
}

export interface StylingRunStats {
  outcome: "success" | "error" | "aborted";
  lockExistingStyles: boolean;
  runId?: string;
  /** File name only, never a path — see `getDocumentFileName`. */
  documentName?: string;
  /** The step that was in progress when the run threw. */
  failedStep?: string;
  profile?: string;
  paragraphs?: number;
  classifiedLines?: number;
  stylesApplied?: number;
  stylesSkipped?: number;
  charStyleRecords?: number;
}

/**
 * Batched steps are numbered (`flattenLinks:flattenBatch01`), so timings are
 * aggregated by the name without its index. Keyed by the raw name instead, a
 * run would contribute ~70 single-use keys and no two runs would line up.
 */
const stepGroup = (name: string): string => name.replace(/\d+$/, "");

function groupedTimings(report: PerfReport): Record<string, string> {
  const groups = new Map<string, { self: number; total: number; count: number }>();

  report.entries.forEach((entry, index) => {
    const key = stepGroup(entry.name);
    const group = groups.get(key) ?? { self: 0, total: 0, count: 0 };
    group.self += selfMs(report.entries, index);
    group.total += entry.durationMs;
    group.count += 1;
    groups.set(key, group);
  });

  const pct = (ms: number) =>
    report.totalMs > 0 ? `${((ms / report.totalMs) * 100).toFixed(1)}%` : "-";

  return Object.fromEntries(
    [...groups]
      .sort(([, a], [, b]) => b.self - a.self)
      .map(([name, { self, total, count }]) => [
        name,
        `self ${formatDuration(self)} (${pct(self)}) of total ${formatDuration(total)}` +
          (count > 1 ? ` across ${count} calls` : ""),
      ])
  );
}

/**
 * Index of the innermost entry enclosing each entry, or -1 for a top-level one.
 *
 * `selfMs` decides nesting by interval containment, and `time()` records in a
 * `finally`, so a child is always earlier in the array than its parent — which
 * is why only later entries are candidates. "Innermost" is the shortest
 * enclosing entry: `applyPara:sync01` sits inside `stage:applyParagraphStyles`,
 * which itself sits inside nothing, and reporting the outermost instead would
 * flatten every stage into one level.
 */
function parentIndices(entries: PerfEntry[]): number[] {
  return entries.map((entry, index) => {
    const end = entry.atMs + entry.durationMs;
    let parent = -1;
    entries.forEach((candidate, candidateIndex) => {
      if (candidateIndex <= index) return;
      const candidateEnd = candidate.atMs + candidate.durationMs;
      if (candidate.atMs > entry.atMs || candidateEnd < end) return;
      if (parent === -1 || candidate.durationMs < entries[parent].durationMs) {
        parent = candidateIndex;
      }
    });
    return parent;
  });
}

/**
 * Replay a finished run as a Sentry transaction with one span per step.
 *
 * Built after the fact from the report's own timings rather than by opening
 * spans as the run progresses: the timings already exist, and threading a span
 * through every stage of `handleClassifyDocument` would mean restructuring it.
 *
 * Nothing is sent unless `tracesSampleRate` is set in `Sentry.init`; the spans
 * are then no-ops and this costs a few object allocations. The trace id comes
 * from the scope's propagation context, which `beginStylingRun` set to the run
 * id, so this transaction joins the API's transaction for the same run.
 */
function replayRunAsTransaction(report: PerfReport, stats: StylingRunStats): void {
  const anchor = report.startedAtEpochMs;
  // A WebView that reports a nonsense timeOrigin would otherwise place the whole
  // run in 1970 and quietly corrupt the trace it is joining.
  if (!Number.isFinite(anchor) || anchor <= 0) return;

  const root = Sentry.startInactiveSpan({
    name: report.label,
    op: "mss.styling_run",
    forceTransaction: true,
    startTime: anchor,
    attributes: {
      "mss.outcome": stats.outcome,
      "mss.lock_existing_styles": stats.lockExistingStyles,
      "mss.total_ms": Math.round(report.totalMs),
      "mss.unattributed_ms": Math.round(report.unattributedMs),
      "mss.steps": report.entries.length,
      ...(stats.profile ? { "mss.profile": stats.profile } : {}),
      ...(stats.documentName ? { "mss.document": stats.documentName } : {}),
      ...(stats.paragraphs != null ? { "mss.paragraphs": stats.paragraphs } : {}),
      ...(stats.classifiedLines != null ? { "mss.classified_lines": stats.classifiedLines } : {}),
      ...(stats.stylesApplied != null ? { "mss.styles_applied": stats.stylesApplied } : {}),
      ...(stats.stylesSkipped != null ? { "mss.styles_skipped": stats.stylesSkipped } : {}),
      ...(stats.charStyleRecords != null
        ? { "mss.char_style_records": stats.charStyleRecords }
        : {}),
      ...(stats.failedStep ? { "mss.failed_step": stats.failedStep } : {}),
    },
  });

  const parents = parentIndices(report.entries);
  const spans: Sentry.Span[] = [];
  // Parents are always later in the array, so building in reverse means an
  // entry's parent span already exists by the time the entry needs it.
  for (let index = report.entries.length - 1; index >= 0; index--) {
    const entry = report.entries[index];
    const parent = parents[index];
    const start = anchor + entry.atMs;
    const span = Sentry.startInactiveSpan({
      name: entry.name,
      op: "mss.styling_step",
      // Named explicitly rather than by making the parent span active: the
      // browser SDK defaults `parentSpanIsAlwaysRootSpan` to true, which would
      // reparent every step onto the transaction and flatten the waterfall. An
      // explicit `parentSpan` is honoured before that rule applies.
      parentSpan: parent === -1 ? root : (spans[parent] ?? root),
      startTime: start,
      attributes: { "mss.self_ms": Math.round(selfMs(report.entries, index)), ...entry.meta },
    });
    span.end(start + entry.durationMs);
    spans[index] = span;
  }

  root.end(anchor + report.totalMs);
}

/**
 * Attach a run's statistics and timings to the Sentry scope and capture a
 * message. The message text carries no run-specific values so that all runs
 * group into one Sentry issue; everything variable lives in the contexts.
 */
export function captureStylingRunCompleted(report: PerfReport, stats: StylingRunStats): void {
  try {
    const email = getUserEmail();
    Sentry.setUser(email ? { email } : null);

    // Tagged, not just context, so it can be searched against the API's event
    // for the same run.
    if (stats.runId) {
      Sentry.setTag("mss.run_id", stats.runId);
    }
    // Tagged too, so Issues can be filtered to one manuscript — a run is usually
    // reported as "the one on <document>", not by its id.
    if (stats.documentName) {
      Sentry.setTag("mss.document", stats.documentName);
    }

    Sentry.setContext("Styling Run", {
      ...stats,
      totalDuration: formatDuration(report.totalMs),
      totalMs: Math.round(report.totalMs),
      unattributedMs: Math.round(report.unattributedMs),
      steps: report.entries.length,
    });
    Sentry.setContext("Styling Run Timings", groupedTimings(report));
    // The same timings as a waterfall. The contexts stay because they are what a
    // single run reads best, and they survive with sampling off.
    replayRunAsTransaction(report, stats);
    // Full per-step detail, including the batch indices the timings context
    // collapses, in the same layout the taskpane's diagnostics block shows.
    Sentry.setContext("Styling Run Report", { text: formatPerfReport(report) });

    Sentry.captureMessage(
      stats.outcome === "success" ? "Styling run completed" : `Styling run ${stats.outcome}`,
      stats.outcome === "error" ? "error" : "info"
    );
  } catch (err) {
    console.warn("Failed to report styling run to Sentry:", err);
  }
}
